import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, X, Send, Copy, ExternalLink, DollarSign, Trash2, FileText, RefreshCw, AlertTriangle } from "lucide-react";

const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const PLATFORM_FEE = 3.0;

export default function SitterInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [netEarned, setNetEarned] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [connectReady, setConnectReady] = useState(true);

  // form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [memo, setMemo] = useState("");
  const [daysDue, setDaysDue] = useState(7);
  const [items, setItems] = useState([{ description: "", amount_dollars: "", quantity: 1 }]);

  const load = async () => {
    setLoading(true);
    try {
      // check Connect status first
      const p = await api.get("/sitters/me");
      setConnectReady(!!p.data.stripe_connect_charges_enabled);
      const { data } = await api.get("/sitters/me/invoices");
      setInvoices(data.results);
      setNetEarned(data.net_earned_cents);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setItems((p) => [...p, { description: "", amount_dollars: "", quantity: 1 }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const subtotal = items.reduce((s, it) => {
    const a = parseFloat(it.amount_dollars);
    const q = parseInt(it.quantity, 10) || 1;
    return s + (isNaN(a) ? 0 : a * q);
  }, 0);
  const familyPays = subtotal + PLATFORM_FEE;
  const youKeep = subtotal;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Family email required");
    const clean = items
      .filter((it) => it.description.trim() && parseFloat(it.amount_dollars) > 0)
      .map((it) => ({
        description: it.description.trim(),
        amount_dollars: parseFloat(it.amount_dollars),
        quantity: parseInt(it.quantity, 10) || 1,
      }));
    if (!clean.length) return toast.error("Add at least one line item");
    setCreating(true);
    try {
      const { data } = await api.post("/sitters/me/invoices", {
        family_email: email.trim().toLowerCase(),
        family_name: name.trim(),
        memo: memo.trim(),
        days_until_due: daysDue,
        line_items: clean,
      });
      toast.success(`Invoice ${data.number} sent to ${data.family_email}`);
      try { await navigator.clipboard.writeText(data.hosted_invoice_url); toast.success("Payment link copied"); } catch (e) {}
      setShowForm(false);
      setEmail(""); setName(""); setMemo("");
      setItems([{ description: "", amount_dollars: "", quantity: 1 }]);
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setCreating(false); }
  };

  const copyLink = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  const voidInv = async (id) => {
    if (!window.confirm("Void this invoice?")) return;
    try { await api.post(`/sitters/me/invoices/${id}/void`); toast.success("Voided"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const statusColor = {
    paid: "bg-sage-100 text-sage-800",
    open: "bg-ochre-100 text-ochre-800",
    void: "bg-red-100 text-red-700",
    draft: "bg-stone2-100 text-stone2-700",
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="sitter-invoices-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <Link to="/sitter" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-heading font-bold text-sage-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-sage-600" /> Invoice a family
            </h1>
            <p className="mt-1 text-sm text-stone2-600">
              Send a Stripe-hosted invoice directly to any family. Money lands in your Stripe account (minus a $3 platform fee).
            </p>
          </div>
          {!showForm && connectReady && (
            <button type="button" onClick={() => setShowForm(true)} data-testid="sitter-invoice-new" className="btn-primary">
              <Plus className="w-4 h-4" /> New invoice
            </button>
          )}
        </div>

        {!connectReady && (
          <div className="mt-6 rounded-xl border-2 border-ochre-300 bg-ochre-50 p-4" data-testid="sitter-connect-required">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-ochre-700 shrink-0 mt-0.5" />
              <div>
                <div className="font-heading font-bold text-sage-900">Stripe payouts not ready yet</div>
                <p className="mt-1 text-sm text-stone2-700">
                  Before you can invoice families, you need to finish setting up Stripe payouts on your dashboard. This is a one-time process (~5 minutes) that lets us send you money.
                </p>
                <Link to="/sitter" className="mt-3 inline-flex btn-primary text-sm">Set up payouts →</Link>
              </div>
            </div>
          </div>
        )}

        {netEarned > 0 && (
          <div className="mt-6 paper-card p-5 max-w-xs">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Net earned (paid invoices)</div>
            <div className="mt-1 text-3xl font-heading font-bold text-sage-900" data-testid="sitter-invoice-net-earned">{dollars(netEarned)}</div>
          </div>
        )}

        {showForm && (
          <form onSubmit={submit} className="mt-6 paper-card p-6 space-y-4" data-testid="sitter-invoice-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="field-label">Family email</label>
                <input required type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="sitter-invoice-email"
                  placeholder="parent@example.com"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
              <div>
                <label className="field-label">Family name <span className="text-stone2-400 font-normal">(optional)</span></label>
                <input type="text" value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="sitter-invoice-name"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
            </div>

            <div>
              <label className="field-label">Line items</label>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_100px_60px_40px] gap-2 items-start" data-testid={`sitter-invoice-item-${i}`}>
                    <input type="text" placeholder="e.g. Sitter service 6-10pm on Jan 15"
                      value={it.description}
                      onChange={(e) => updateItem(i, "description", e.target.value)}
                      className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                    <input type="number" step="0.01" min="0" placeholder="$/unit"
                      value={it.amount_dollars}
                      onChange={(e) => updateItem(i, "amount_dollars", e.target.value)}
                      className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                    <input type="number" min="1" placeholder="Qty"
                      value={it.quantity}
                      onChange={(e) => updateItem(i, "quantity", e.target.value)}
                      className="rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500 text-center" />
                    <button type="button" onClick={() => removeItem(i)} disabled={items.length === 1}
                      className="text-stone2-400 hover:text-red-600 disabled:opacity-30 p-2">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={addItem} data-testid="sitter-invoice-add-item"
                className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-sage-700 hover:text-sage-900">
                <Plus className="w-3.5 h-3.5" /> Add another line
              </button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="field-label">Days until due</label>
                <input type="number" min="0" max="90" value={daysDue}
                  onChange={(e) => setDaysDue(parseInt(e.target.value, 10) || 0)}
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="field-label">Memo <span className="text-stone2-400 font-normal">(shown on invoice)</span></label>
                <input type="text" value={memo} maxLength={300}
                  onChange={(e) => setMemo(e.target.value)}
                  data-testid="sitter-invoice-memo"
                  placeholder="Thanks for choosing me!"
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>
            </div>

            {/* Transparent fee breakdown */}
            <div className="rounded-xl bg-sage-50 border border-sage-200 p-4 text-sm space-y-1">
              <div className="flex justify-between">
                <span>Subtotal (your services)</span>
                <span className="font-mono">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-stone2-600 text-xs">
                <span>+ AutismBabysitters platform service fee</span>
                <span className="font-mono">${PLATFORM_FEE.toFixed(2)}</span>
              </div>
              <div className="border-t border-sage-200 pt-1 flex justify-between font-heading font-bold text-sage-900">
                <span>Family pays</span>
                <span className="font-mono" data-testid="sitter-invoice-family-pays">${familyPays.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sage-700 font-semibold">
                <span>You keep (after Stripe fees & platform fee)</span>
                <span className="font-mono" data-testid="sitter-invoice-you-keep">${youKeep.toFixed(2)}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button type="submit" disabled={creating} data-testid="sitter-invoice-submit" className="btn-primary">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send invoice
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-outline-sage">Cancel</button>
            </div>
          </form>
        )}

        <div className="mt-8 paper-card overflow-hidden">
          <div className="px-5 py-3 border-b border-stone2-200 flex items-center justify-between">
            <div className="font-heading font-semibold text-sage-900">Your invoices</div>
            <button type="button" onClick={load} className="text-xs text-sage-700 font-semibold inline-flex items-center gap-1 hover:text-sage-900">
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          {invoices.length === 0 ? (
            <div className="p-10 text-center text-stone2-500 text-sm">
              No invoices yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sitter-invoices-table">
                <thead className="bg-sage-50 text-sage-900">
                  <tr>
                    <th className="text-left px-4 py-3 font-heading font-semibold">#</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Family</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Total</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">You keep</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-heading font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone2-100">
                  {invoices.map((inv) => {
                    const youKeepCents = (inv.amount_paid_cents ?? inv.subtotal_cents ?? 0);
                    return (
                      <tr key={inv.id} data-testid={`sitter-invoice-row-${inv.id}`} className="hover:bg-sage-50/60">
                        <td className="px-4 py-3 font-mono text-xs text-stone2-600">{inv.number || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-sage-900">{inv.family_name || "—"}</div>
                          <div className="text-xs text-stone2-500">{inv.family_email}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-sage-900">{dollars(inv.amount_due_cents)}</td>
                        <td className="px-4 py-3 text-sage-700">
                          {dollars(inv.subtotal_cents || 0)}
                          <div className="text-[10px] text-stone2-400">−$3 platform fee</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${statusColor[inv.status] || "bg-stone2-100"}`}>
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {inv.hosted_invoice_url && (
                              <button type="button" onClick={() => copyLink(inv.hosted_invoice_url)}
                                data-testid={`sitter-invoice-copy-${inv.id}`}
                                className="text-xs font-semibold px-2 py-1 rounded-md bg-sage-600 text-white hover:bg-sage-700 inline-flex items-center gap-1">
                                <Copy className="w-3 h-3" /> Link
                              </button>
                            )}
                            {inv.status === "open" && (
                              <button type="button" onClick={() => voidInv(inv.id)}
                                data-testid={`sitter-invoice-void-${inv.id}`}
                                className="text-xs font-semibold px-2 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
